import { useEffect, useRef, useState } from 'react';
import {
  useSubscription,
  usePreviewUpgrade,
  useUpgradeMutation,
} from '@/features/billing/queries';
import {
  classifyUpgradeError,
  type UpgradeErrorAction,
} from '@/features/billing/plan-helpers';
import {
  checkoutCodeErrorMessage,
  isDiscountCodeError,
} from '@/features/billing/checkout-code-helpers';
import type { BillingPlan, Subscription } from '@/lib/billing';

/**
 * The self-serve upgrade flow (P2, spec §8) as a single hook, so the
 * `UpgradePlansCard` stays a thin presentational orchestrator under the 250-line
 * limit and the multi-step state machine is testable through the card.
 *
 * Phases (only meaningful while `activePlan` is set — i.e. the confirm dialog is
 * open):
 *   - `confirm`    — showing the proration preview; the owner can confirm.
 *   - `processing` — `PATCH` acknowledged; polling the subscription until the
 *                    webhook flips the plan (`useSubscription({ pollUntilPlanId })`).
 *   - `done`       — the polled subscription reflected the target plan. STICKY:
 *                    once latched it never reverts, so a later stale refetch
 *                    returning the old plan can't flip `done` → `processing`.
 *   - `timeout`    — the webhook didn't land within the poll deadline; the flow
 *                    bails out to a terminal state with a manual refresh + portal
 *                    affordance instead of spinning "Applying…" forever.
 *
 * The subscription read is owned here (single observer, shared query key) and
 * returned to the card for row-mode derivation + the loading/error states.
 *
 * ## Promo code
 *
 * Two pieces of state, deliberately: `codeDraft` is what's in the input, and
 * `appliedCode` is the committed value that feeds the preview's query key.
 * "Apply" copies one into the other, which re-runs the preview WITH the discount
 * so Paddle's real discounted figures land in the proration summary — the client
 * never estimates a saving. "Remove code" clears `appliedCode`, and the
 * undiscounted quote reappears instantly because it is still cached under the
 * no-code key.
 *
 * A preview that fails with a `Billing.Discount.*` code is a REJECTED CODE, not
 * a broken quote: it surfaces as an inline field error (`promo.error`) beside a
 * working "Remove code", while a genuine preview failure (`041006`) keeps the
 * dialog's `RetryBlock` path. Both leave `preview.isError` true, which is what
 * keeps `confirmDisabled` blocking a confirm under a bad code.
 */
export type UpgradePhase = 'confirm' | 'processing' | 'done' | 'timeout';

/** Poll cadence + bail-out deadline for the webhook-driven plan flip. */
const POLL_TIMEOUT_MS = 60_000;

type Params = {
  isOwner: boolean;
  /** Called for `041002 NoPaddleSubscription` — fall back to first-time checkout. */
  onNeedsCheckout: (plan: BillingPlan) => void;
};

export function usePlanUpgrade({ isOwner, onNeedsCheckout }: Params) {
  const [activePlan, setActivePlan] = useState<BillingPlan | null>(null);
  const [processingPlanId, setProcessingPlanId] = useState<number | null>(null);
  const [pollStartedAt, setPollStartedAt] = useState<number | null>(null);
  const [latchedDone, setLatchedDone] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [notice, setNotice] = useState<UpgradeErrorAction | null>(null);
  // What's in the promo input vs. what the preview is actually quoting.
  const [codeDraft, setCodeDraft] = useState('');
  const [appliedCode, setAppliedCode] = useState<string | null>(null);
  // A discount rejection raised by the CONFIRM (not the preview). Separate state
  // because the preview succeeded — `041310` is reachable even when the code
  // previewed cleanly and `activeDiscount` reported nothing (that hint is also
  // null whenever Paddle is unreachable).
  const [submitCodeError, setSubmitCodeError] = useState<string | null>(null);

  // Mirror the live `activePlan` into a ref so an in-flight `confirmUpgrade`
  // await can tell whether the dialog was closed / switched mid-request.
  const activePlanRef = useRef<BillingPlan | null>(activePlan);
  useEffect(() => {
    activePlanRef.current = activePlan;
  }, [activePlan]);

  const upgrade = useUpgradeMutation();

  // Poll only while genuinely processing — stop once success latches or the
  // deadline trips, so the interval doesn't run past the terminal state.
  const shouldPoll = processingPlanId != null && !latchedDone && !timedOut;
  const subscriptionQuery = useSubscription({
    enabled: isOwner,
    pollUntilPlanId: shouldPoll ? processingPlanId : null,
    pollDeadlineAt:
      pollStartedAt != null ? pollStartedAt + POLL_TIMEOUT_MS : null,
  });
  const subscription: Subscription | undefined = subscriptionQuery.data;

  const arrived =
    processingPlanId != null && subscription?.plan.id === processingPlanId;

  // Latch success the instant the poll observes the target plan. Deriving `done`
  // from live subscription data would let a subsequent stale refetch (old plan)
  // flip it back to `processing`; the latch makes `done` sticky (F11).
  useEffect(() => {
    if (arrived) setLatchedDone(true);
  }, [arrived]);

  // Bail-out timer: while processing (and not yet done), arm a one-shot deadline
  // that transitions to the terminal `timeout` phase. This is a UI deadline, not
  // a fetch, so a timer effect (not TanStack Query) owns it (F2).
  useEffect(() => {
    if (processingPlanId == null || latchedDone || timedOut) return;
    const timer = setTimeout(() => setTimedOut(true), POLL_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [processingPlanId, latchedDone, timedOut]);

  const phase: UpgradePhase = latchedDone
    ? 'done'
    : timedOut
      ? 'timeout'
      : processingPlanId != null
        ? 'processing'
        : 'confirm';

  const preview = usePreviewUpgrade(activePlan?.id ?? null, {
    enabled: activePlan != null && phase === 'confirm',
    code: appliedCode,
  });

  // A discount-family failure on the preview is the applied code being rejected;
  // anything else is a genuine quote failure and stays on the `RetryBlock` path.
  const previewCodeError =
    preview.isError && isDiscountCodeError(preview.error)
      ? checkoutCodeErrorMessage(preview.error, activePlan)
      : null;
  const codeError = previewCodeError ?? submitCodeError;

  function resetPromo() {
    setCodeDraft('');
    setAppliedCode(null);
    setSubmitCodeError(null);
  }

  function startUpgrade(plan: BillingPlan) {
    setNotice(null);
    setProcessingPlanId(null);
    setPollStartedAt(null);
    setLatchedDone(false);
    setTimedOut(false);
    resetPromo();
    setActivePlan(plan);
  }

  function closeDialog() {
    setActivePlan(null);
    setProcessingPlanId(null);
    setPollStartedAt(null);
    setLatchedDone(false);
    setTimedOut(false);
    setNotice(null);
    resetPromo();
  }

  /** Commit the draft — the key change is what re-quotes with the discount. */
  function applyCode() {
    const trimmed = codeDraft.trim();
    if (trimmed === '') return;
    setSubmitCodeError(null);
    setAppliedCode(trimmed);
  }

  async function confirmUpgrade() {
    const plan = activePlan;
    if (!plan) return;
    setNotice(null);
    setSubmitCodeError(null);
    try {
      await upgrade.mutateAsync({ planId: plan.id, ...(appliedCode ? { code: appliedCode } : {}) });
      // The dialog may have been closed (or switched to another plan) while the
      // PATCH was in flight — don't resurrect processing state for a dismissed
      // flow (F11).
      if (activePlanRef.current?.id !== plan.id) return;
      // The webhook applies the real change; poll the subscription until it lands.
      setPollStartedAt(Date.now());
      setProcessingPlanId(plan.id);
    } catch (err) {
      if (activePlanRef.current?.id !== plan.id) return;
      // The code was rejected at submit time (typically `041310` racing a stale
      // or Paddle-unreachable `activeDiscount` hint). Point at the field the
      // user can act on rather than the generic dead-end notice.
      if (isDiscountCodeError(err)) {
        setSubmitCodeError(checkoutCodeErrorMessage(err, plan));
        return;
      }
      const action = classifyUpgradeError(err);
      if (action.kind === 'checkout') {
        closeDialog();
        onNeedsCheckout(plan);
        return;
      }
      setNotice(action);
    }
  }

  return {
    // Subscription read (row-mode derivation + card loading/error states).
    subscription,
    isSubscriptionLoading: subscriptionQuery.isLoading,
    isSubscriptionError: subscriptionQuery.isError,
    // Confirm-dialog flow.
    activePlan,
    phase,
    preview: {
      isLoading: preview.isLoading,
      isError: preview.isError,
      error: preview.error,
      data: preview.data,
      refetch: () => void preview.refetch(),
    },
    confirming: upgrade.isPending,
    notice,
    /** Promo-code field state for `UpgradePromoField` (confirm phase only). */
    promo: {
      draft: codeDraft,
      setDraft: setCodeDraft,
      appliedCode,
      /** Inline field error — a REJECTED code, never a broken preview. */
      error: codeError,
      apply: applyCode,
      remove: resetPromo,
      /** Display hint: the discount already live at Paddle, or null. */
      activeDiscount: subscription?.activeDiscount ?? null,
    },
    startUpgrade,
    confirmUpgrade,
    closeDialog,
    /** Manual refetch for the `timeout` phase — recovers to `done` if it lands. */
    refresh: () => void subscriptionQuery.refetch(),
  } as const;
}

export type PlanUpgradeFlow = ReturnType<typeof usePlanUpgrade>;
